import { PROGRAMME_TYPES } from "./care-kind.js";
const day = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short" });
const time = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit" });
const longDay = new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
});
const weekdayLong = new Intl.DateTimeFormat("en-GB", { weekday: "long" });
const weekdayShort = new Intl.DateTimeFormat("en-GB", { weekday: "short" });
const date = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric" });
/** "4 November 2026", for programme end dates that can be months away. */
export const formatDate = (iso) => date.format(new Date(iso));
/** "Tue 4 Nov" */
export const formatDay = (iso) => day.format(new Date(iso));
/** "Tuesday 4 November" */
export const formatLongDay = (iso) => longDay.format(new Date(iso));
/** "Tue" */
export const formatWeekday = (iso) => weekdayShort.format(new Date(iso));
/** "09:00" */
export const formatTime = (iso) => time.format(new Date(iso));
/** "Tuesday 4 November, 09:00 to 09:45" */
export const formatSession = (startsAt, endsAt) => `${longDay.format(new Date(startsAt))}, ${formatTime(startsAt)} to ${formatTime(endsAt)}`;
/** The local calendar day an instant falls on, for grouping slots. */
export const localDayKey = (iso) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};
export const SESSION_STATUS_LABEL = {
    scheduled: "Booked",
    completed: "Completed",
    cancelled: "Cancelled",
    coachee_no_show: "Missed",
    coach_no_show: "Coach missed, credited back",
};
export const COACHING_TYPE_LABEL = Object.fromEntries(Object.entries(PROGRAMME_TYPES).map(([type, t]) => [type, t.label]));
/** "Today", "Yesterday", or "Tue 4 Nov", for a divider in a conversation. */
export const dayLabel = (iso, now = new Date()) => {
    const key = localDayKey(iso);
    if (key === localDayKey(now.toISOString()))
        return "Today";
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    return key === localDayKey(yesterday.toISOString()) ? "Yesterday" : formatDay(iso);
};
/** "Today", "Tomorrow", or the weekday, for the day a session falls on. */
export const relativeDay = (iso, now) => {
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (localDayKey(iso) === localDayKey(new Date(now).toISOString()))
        return "Today";
    if (localDayKey(iso) === localDayKey(tomorrow.toISOString()))
        return "Tomorrow";
    return weekdayLong.format(new Date(iso));
};
/** "Daniel", from "Daniel Reyes". */
export const firstName = (name) => name.split(" ")[0] ?? name;
const MINUTE = 60_000;
/** How soon a session is, in words: "Starts in 25 minutes", "Under way until 14:45", "Tomorrow at 14:00". */
export function whenLabel(startsAt, endsAt, now) {
    const start = Date.parse(startsAt);
    const end = Date.parse(endsAt);
    if (now >= start && now < end)
        return `Under way until ${formatTime(endsAt)}`;
    const minutes = Math.round((start - now) / MINUTE);
    if (minutes <= 0)
        return "";
    if (minutes < 60)
        return `Starts in ${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
    const today = localDayKey(new Date(now).toISOString());
    if (localDayKey(startsAt) === today)
        return `Today at ${formatTime(startsAt)}`;
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (localDayKey(startsAt) === localDayKey(tomorrow.toISOString())) {
        return `Tomorrow at ${formatTime(startsAt)}`;
    }
    return "";
}
/** The meeting link is the main thing to do from ten minutes before until the end. */
export const isJoinable = (startsAt, endsAt, now) => now >= Date.parse(startsAt) - 10 * MINUTE && now < Date.parse(endsAt);
/** "London", from "Europe/London": a zone as a city, the reader's own when none is given. */
export function timeZoneCity(zone = Intl.DateTimeFormat().resolvedOptions().timeZone) {
    return zone.split("/").pop()?.replace(/_/g, " ") ?? zone;
}
/** "1 session", "3 sessions". */
export const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
/** How long a session runs. */
export const SESSION_MINUTES = 45;
/** Booked sessions still to come, soonest first, and the rest, most recent first. */
export function splitSessions(sessions, now) {
    const upcoming = [];
    const past = [];
    for (const s of sessions) {
        if (s.status === "scheduled" && Date.parse(s.endsAt) > now)
            upcoming.push(s);
        else
            past.push(s);
    }
    upcoming.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    past.sort((a, b) => b.startsAt.localeCompare(a.startsAt));
    return { upcoming, past };
}
/** Open times grouped into the local days they fall on, in order. */
export function slotsByDay(slots) {
    const days = new Map();
    for (const s of slots) {
        const key = localDayKey(s.start);
        const day = days.get(key);
        if (day)
            day.push(s);
        else
            days.set(key, [s]);
    }
    return [...days.values()];
}
/** Days of open times grouped into Monday-to-Sunday weeks. */
export function slotsByWeek(slots) {
    const weeks = new Map();
    for (const day of slotsByDay(slots)) {
        const monday = new Date(day[0].start);
        monday.setHours(0, 0, 0, 0);
        monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
        const key = monday.toDateString();
        const week = weeks.get(key);
        if (week)
            week.push(day);
        else
            weeks.set(key, [day]);
    }
    return [...weeks.values()];
}
//# sourceMappingURL=coaching-format.js.map